import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronLeft,
  CircleAlert,
  Clock3,
  FileCheck2,
  Gavel,
  LoaderCircle,
  LogOut,
  RefreshCw,
  Search,
  ShieldCheck,
  ShieldOff,
  UserRoundCog,
  X,
} from "lucide-react";
import { ApiFailure, api } from "./api.js";
import type { MysqlMe } from "./auth-types.js";
import { BrandMark } from "./BrandMark.js";

type PlatformRole = "PLATFORM_REVIEWER" | "PLATFORM_REVIEW_ADMIN" | "PLATFORM_SECURITY_ADMIN" | "PLATFORM_AUDITOR";
type RoleAction = "GRANT" | "REVOKE" | "RESTORE";
type RequestStatus = "PENDING" | "APPROVED" | "REJECTED" | "EXPIRED" | "CANCELLED" | "APPLIED";

interface PlatformAccount {
  id: string;
  maskedEmail: string;
  accountStatus: "ACTIVE" | "SUSPENDED" | "CLOSED";
  activeRoles: PlatformRole[];
}

interface RoleChangeRequest {
  id: string;
  targetAccount: { id: string; maskedEmail: string; activeRoles: PlatformRole[] };
  role: PlatformRole;
  action: RoleAction;
  status: RequestStatus;
  requester: { id: string; maskedEmail: string };
  decider: { id: string; maskedEmail: string } | null;
  reasonCode: string;
  requestedAt: string;
  expiresAt: string;
  decidedAt: string | null;
  appliedAt: string | null;
  canCurrentActorDecide: boolean;
}

interface RevocationRequest {
  id: string;
  subjectType: "PERSONAL" | "ORGANIZATION";
  personalIdentityId: string | null;
  organizationId: string | null;
  revocationType: "USER_REQUESTED" | "RISK_CONTROL" | "LEGAL_OR_REGULATORY" | "ORGANIZATION_DISSOLVED";
  status: "PENDING_REVIEW" | "COOLING" | "APPROVED" | "REJECTED" | "CANCELLED" | "APPLIED";
  version: number;
  reasonCode: string;
  requestedByAccountId: string;
  requestedAt: string;
  coolingUntil: string | null;
  appliedAt: string | null;
  canCurrentActorDecide: boolean;
}

interface MfaResetRequest {
  id: string; targetAccountId: string;
  status: "SUBMITTED" | "IDENTITY_VERIFIED" | "PENDING_APPROVAL" | "APPROVED" | "COOLING" | "REJECTED" | "CANCELLED" | "EXPIRED" | "APPLIED";
  version: number; requestedAt: string; identityReviewedByAccountId: string | null;
  initiatedBySecurityAdminId: string | null; approvedBySecurityAdminId: string | null;
  notificationsVerifiedAt: string | null; coolingUntil: string | null; appliedAt: string | null;
  canCurrentActorInitiate: boolean; canCurrentActorDecide: boolean;
}

type GateComponent = "OBJECT_STORAGE" | "MALWARE_SCANNER" | "KMS_HSM" | "MFA_NOTIFICATION" | "CONSORTIUM_CHAIN";
interface ProductionGate {
  id: string; component: GateComponent; environment: "STAGING" | "PRODUCTION"; provider: string;
  evidenceDigest: string; evidenceRef: string; status: "PENDING_APPROVAL" | "PASSED" | "REJECTED" | "REVOKED" | "EXPIRED";
  version: number; requestedByAccountId: string; approvedByAccountId: string | null;
  requestedAt: string; decidedAt: string | null; expiresAt: string;
  matchesCurrentConfiguration: boolean; canCurrentActorDecide: boolean;
}
interface GateReadiness {
  environment: "STAGING" | "PRODUCTION"; ready: boolean;
  components: Array<{ component: GateComponent; provider: string; configured: boolean; gatePassed: boolean; runtimeConnected: boolean; reason: string }>;
}

const roleNames: Record<PlatformRole, string> = {
  PLATFORM_REVIEWER: "身份审核员",
  PLATFORM_REVIEW_ADMIN: "审核管理员",
  PLATFORM_SECURITY_ADMIN: "安全管理员",
  PLATFORM_AUDITOR: "平台审计员",
};

const actionNames: Record<RoleAction, string> = { GRANT: "授予", REVOKE: "回收", RESTORE: "恢复" };
const statusNames: Record<RequestStatus, string> = {
  PENDING: "等待第二人复核",
  APPROVED: "已同意，待应用",
  REJECTED: "已拒绝",
  EXPIRED: "已过期",
  CANCELLED: "已取消",
  APPLIED: "已生效",
};

function shortId(value: string): string {
  return value.length > 18 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value;
}

function formatTime(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function ErrorNotice({ error }: { error: ApiFailure }): React.JSX.Element {
  return <div className="error-notice" role="alert"><CircleAlert /><span><b>{error.message}</b><small>{error.code}{error.requestId ? ` · 请求 ${shortId(error.requestId)}` : ""}</small></span></div>;
}

export function PlatformSecurityWorkbench({
  me,
  onBack,
  onOpenEvidence,
  onOpenReview,
  onLogout,
}: {
  me: MysqlMe;
  onBack: () => void;
  onOpenEvidence: () => void;
  onOpenReview: () => void;
  onLogout: () => void;
}): React.JSX.Element {
  const [requests, setRequests] = useState<RoleChangeRequest[]>([]);
  const [statusFilter, setStatusFilter] = useState<RequestStatus | "ALL">("PENDING");
  const [query, setQuery] = useState("");
  const [accounts, setAccounts] = useState<PlatformAccount[]>([]);
  const [selected, setSelected] = useState<PlatformAccount | null>(null);
  const [role, setRole] = useState<PlatformRole>("PLATFORM_REVIEWER");
  const [action, setAction] = useState<RoleAction>("GRANT");
  const [reasonCode, setReasonCode] = useState("DUTY_ASSIGNMENT");
  const [reason, setReason] = useState("");
  const [rejectReasonCode, setRejectReasonCode] = useState("POLICY_NOT_MET");
  const [rejectReason, setRejectReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<ApiFailure | null>(null);
  const [success, setSuccess] = useState("");
  const [revocations, setRevocations] = useState<RevocationRequest[]>([]);
  const [revocationSubjectType, setRevocationSubjectType] = useState<"PERSONAL" | "ORGANIZATION">("PERSONAL");
  const [revocationSubjectId, setRevocationSubjectId] = useState("");
  const [revocationType, setRevocationType] = useState<"RISK_CONTROL" | "LEGAL_OR_REGULATORY" | "ORGANIZATION_DISSOLVED">("RISK_CONTROL");
  const [mfaResets, setMfaResets] = useState<MfaResetRequest[]>([]);
  const [productionGates, setProductionGates] = useState<ProductionGate[]>([]);
  const [gateReadiness, setGateReadiness] = useState<GateReadiness | null>(null);
  const [gateComponent, setGateComponent] = useState<GateComponent>("OBJECT_STORAGE");
  const [gateEnvironment, setGateEnvironment] = useState<"STAGING" | "PRODUCTION">("STAGING");
  const [gateEvidenceDigest, setGateEvidenceDigest] = useState("");
  const [gateEvidenceRef, setGateEvidenceRef] = useState("");

  const canManage = me.capabilities.managePlatformSecurity;
  const pending = useMemo(() => requests.filter((item) => item.status === "PENDING").length, [requests]);
  const loadRequests = useCallback(async (quiet = false): Promise<void> => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const parameters = new URLSearchParams({ limit: "200" });
      if (statusFilter !== "ALL") parameters.set("status", statusFilter);
      const response = await api<RoleChangeRequest[]>(`/v2/platform/role-change-requests?${parameters}`);
      setRequests(response.data);
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法读取平台角色变更队列。"));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { void loadRequests(); }, [loadRequests]);
  const loadRevocations = useCallback(async (): Promise<void> => {
    try { setRevocations((await api<RevocationRequest[]>("/v2/platform/identity-revocation-requests")).data); } catch { setRevocations([]); }
  }, []);
  useEffect(() => { if (canManage) void loadRevocations(); }, [canManage, loadRevocations]);
  const loadD09D10 = useCallback(async (): Promise<void> => {
    try {
      const [resets, gates] = await Promise.all([
        api<MfaResetRequest[]>("/v2/platform/mfa-reset-requests"),
        api<ProductionGate[]>("/v2/platform/production-gates?environment=PRODUCTION"),
      ]);
      setMfaResets(resets.data); setProductionGates(gates.data); setGateReadiness(gates.meta as GateReadiness);
    } catch { setMfaResets([]); setProductionGates([]); setGateReadiness(null); }
  }, []);
  useEffect(() => { void loadD09D10(); }, [loadD09D10]);

  const searchAccounts = async (): Promise<void> => {
    if (query.trim().length < 2) return;
    setBusy("search");
    setError(null);
    try {
      const response = await api<PlatformAccount[]>(`/v2/platform/accounts?query=${encodeURIComponent(query.trim())}&limit=20`);
      setAccounts(response.data);
      if (response.data.length === 1) setSelected(response.data[0]!);
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法检索平台账号。"));
    } finally {
      setBusy(null);
    }
  };

  const createRequest = async (): Promise<void> => {
    if (!selected || reason.trim().length < 2) return;
    setBusy("create");
    setError(null);
    setSuccess("");
    try {
      await api<RoleChangeRequest>("/v2/platform/role-change-requests", {
        method: "POST",
        headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({ targetAccountId: selected.id, role, action, reasonCode, reason }),
      });
      setSuccess("变更申请已创建，必须由另一名安全管理员在 24 小时内复核。");
      setReason("");
      setStatusFilter("PENDING");
      await loadRequests(true);
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法创建平台角色变更申请。"));
    } finally {
      setBusy(null);
    }
  };

  const decide = async (item: RoleChangeRequest, decision: "APPROVE" | "REJECT"): Promise<void> => {
    if (decision === "REJECT" && rejectReason.trim().length < 2) return;
    setBusy(`${decision}:${item.id}`);
    setError(null);
    setSuccess("");
    try {
      const response = await api<RoleChangeRequest>(`/v2/platform/role-change-requests/${item.id}/decision`, {
        method: "POST",
        headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify(decision === "APPROVE"
          ? { decision }
          : { decision, reasonCode: rejectReasonCode, reason: rejectReason }),
      });
      setSuccess(response.data.status === "APPLIED"
        ? `${actionNames[response.data.action]} ${roleNames[response.data.role]} 已原子生效。`
        : "申请已拒绝并保留审计记录。");
      setRejectReason("");
      await loadRequests(true);
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法完成复核决定。"));
    } finally {
      setBusy(null);
    }
  };

  const createRevocation = async (): Promise<void> => {
    if (!revocationSubjectId.trim() || reason.trim().length < 2) return;
    setBusy("create-revocation"); setError(null); setSuccess("");
    try {
      await api<RevocationRequest>("/v2/platform/identity-revocation-requests", {
        method: "POST", headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({ subjectType: revocationSubjectType, subjectId: revocationSubjectId.trim(), revocationType, reasonCode, reason }),
      });
      setSuccess("强制撤销申请已创建，必须由另一名安全管理员复核后才会冻结身份与 Tenant。");
      setReason(""); setRevocationSubjectId(""); await loadRevocations();
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法创建身份撤销申请。")); }
    finally { setBusy(null); }
  };

  const decideRevocation = async (item: RevocationRequest, decision: "APPROVE" | "REJECT"): Promise<void> => {
    setBusy(`revocation-${decision}:${item.id}`); setError(null); setSuccess("");
    try {
      const response = await api<RevocationRequest>(`/v2/platform/identity-revocation-requests/${item.id}/decision`, {
        method: "POST", headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({ decision, expectedVersion: item.version, ...(decision === "REJECT" ? { reason: rejectReason } : {}) }),
      });
      setSuccess(response.data.status === "APPLIED" ? "身份、Tenant、成员写权与对应活跃会话已原子回收。" : "强制撤销申请已拒绝。");
      await loadRevocations();
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法复核身份撤销申请。")); }
    finally { setBusy(null); }
  };

  const advanceMfaReset = async (item: MfaResetRequest, action: "INITIATE" | "APPROVE" | "REJECT"): Promise<void> => {
    setBusy(`mfa-reset:${action}:${item.id}`); setError(null); setSuccess("");
    try {
      await api(action === "INITIATE" ? `/v2/platform/mfa-reset-requests/${item.id}/initiate` : `/v2/platform/mfa-reset-requests/${item.id}/decision`, {
        method: "POST", headers: action === "INITIATE" ? undefined : { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({ expectedVersion: item.version, ...(action === "INITIATE" ? {} : { decision: action }) }),
      });
      setSuccess(action === "INITIATE" ? "MFA 重置已提交第二名安全管理员复核。" : action === "APPROVE" ? "通知门禁通过后已进入 24 小时冷静期。" : "MFA 重置已拒绝。");
      await loadD09D10();
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法处理 MFA 重置申请。")); }
    finally { setBusy(null); }
  };

  const createProductionGate = async (): Promise<void> => {
    if (!/^[0-9a-f]{64}$/u.test(gateEvidenceDigest) || gateEvidenceRef.trim().length < 3) return;
    setBusy("production-gate:create"); setError(null); setSuccess("");
    try {
      await api("/v2/platform/production-gates", { method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify({ component: gateComponent, environment: gateEnvironment, evidenceDigest: gateEvidenceDigest, evidenceRef: gateEvidenceRef }) });
      setSuccess("POC 证据已登记，必须由另一名安全管理员确认，且批准时配置指纹必须保持一致。"); setGateEvidenceDigest(""); setGateEvidenceRef(""); await loadD09D10();
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法创建生产门禁申请。")); }
    finally { setBusy(null); }
  };

  const decideProductionGate = async (item: ProductionGate, decision: "PASS" | "REJECT"): Promise<void> => {
    setBusy(`production-gate:${item.id}`); setError(null); setSuccess("");
    try {
      await api(`/v2/platform/production-gates/${item.id}/decision`, { method: "POST", headers: { "idempotency-key": crypto.randomUUID() }, body: JSON.stringify({ decision, expectedVersion: item.version }) });
      setSuccess(decision === "PASS" ? "POC 门禁证据已由第二人批准；运行适配器仍须实际接通才能进入生产。" : "POC 门禁证据已拒绝。"); await loadD09D10();
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法复核生产门禁。")); }
    finally { setBusy(null); }
  };

  const logout = async (): Promise<void> => {
    try { await api<void>("/v2/auth/session", { method: "DELETE" }); } finally { onLogout(); }
  };

  return (
    <div className="workspace security-workspace">
      <header className="workspace-header">
        <div className="brand onboarding-brand"><BrandMark /><span><b>链脉信枢</b><small>PLATFORM SECURITY CONTROL</small></span></div>
        <div className="workspace-actions">
          <button className="secondary-button" onClick={onBack}><ChevronLeft />身份中心</button>
          {me.trustedContext && <button className="secondary-button" onClick={onOpenEvidence}><FileCheck2 />存证工作台</button>}
          {me.capabilities.reviewIdentities && <button className="secondary-button" onClick={onOpenReview}><ShieldCheck />审核工作台</button>}
          <button className="secondary-button" disabled={loading} onClick={() => void loadRequests()}>{loading ? <LoaderCircle className="spin" /> : <RefreshCw />}刷新</button>
          <button className="icon-button" aria-label="退出登录" onClick={() => void logout()}><LogOut /></button>
        </div>
      </header>

      <main className="workspace-main security-main">
        <section className="welcome-band security-welcome">
          <div><span className="eyebrow"><ShieldCheck />双人授权 · 数据库原子事务</span><h1>平台安全管理工作台</h1><p>高权角色的申请人与复核人必须不同，目标账号本人不能参与审批；决定前会再次校验实时权限。</p></div>
          <div className="security-summary"><span><small>待复核</small><b>{loading ? "—" : pending}</b></span><span><small>当前账号</small><b>{me.platformRoles.includes("PLATFORM_SECURITY_ADMIN") ? "安全管理员" : "只读审计员"}</b></span></div>
        </section>

        <section className="security-invariants">
          <article><ShieldCheck /><span><b>双人控制</b><small>发起人、复核人、目标账号三方隔离</small></span></article>
          <article><ShieldOff /><span><b>最后两人保护</b><small>任何事务不得把活跃安全管理员降至 2 人以下</small></span></article>
          <article><Clock3 /><span><b>24 小时失效</b><small>由数据库时钟结算，后台任务自动关闭过期申请</small></span></article>
        </section>

        {error && <ErrorNotice error={error} />}
        {success && <div className="success-notice"><Check /><span><b>{success}</b><small>角色投影、会话回收、事件和审计已在同一事务结算。</small></span></div>}

        <section className="security-grid">
          {canManage && <article className="panel security-create-panel">
            <div className="panel-header"><div><span className="heading-icon"><UserRoundCog /></span><span><h2>发起角色变更</h2><p>先检索并确认目标账号，再提交第二人复核。</p></span></div></div>
            <div className="security-search"><input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void searchAccounts(); }} placeholder="输入账号 UUID 前缀或脱敏邮箱" /><button className="secondary-button" disabled={query.trim().length < 2 || busy === "search"} onClick={() => void searchAccounts()}>{busy === "search" ? <LoaderCircle className="spin" /> : <Search />}检索</button></div>
            {accounts.length > 0 && <div className="security-account-results">{accounts.map((account) => <button key={account.id} className={selected?.id === account.id ? "selected" : ""} onClick={() => setSelected(account)}><span><b>{account.maskedEmail}</b><code>{shortId(account.id)}</code></span><small>{account.activeRoles.length ? account.activeRoles.map((item) => roleNames[item]).join("、") : "无平台角色"}</small></button>)}</div>}
            {selected && <div className="security-target"><span><small>已选目标</small><b>{selected.maskedEmail}</b><code>{selected.id}</code></span><i>{selected.accountStatus}</i></div>}
            <div className="security-form-grid">
              <label>变更动作<select value={action} onChange={(event) => setAction(event.target.value as RoleAction)}><option value="GRANT">授予新角色</option><option value="REVOKE">回收现有角色</option><option value="RESTORE">恢复已回收角色</option></select></label>
              <label>平台角色<select value={role} onChange={(event) => setRole(event.target.value as PlatformRole)}>{Object.entries(roleNames).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <label>原因代码<input value={reasonCode} maxLength={64} onChange={(event) => setReasonCode(event.target.value.toUpperCase().replace(/[^A-Z0-9_:-]/gu, ""))} /></label>
              <label className="wide">申请说明<textarea value={reason} minLength={2} maxLength={1000} onChange={(event) => setReason(event.target.value)} placeholder="说明授权依据、职责范围或回收原因；正文加密保存" /></label>
            </div>
            {action === "REVOKE" && <div className="warning-notice"><CircleAlert /><span><b>回收将注销目标账号全部活跃会话</b><small>目标需重新登录；新会话会按最新平台角色重新计算权限。</small></span></div>}
            {role === "PLATFORM_SECURITY_ADMIN" && action === "REVOKE" && <small className="decision-hint"><ShieldOff />服务端将在事务锁内重新计数；最后两名安全管理员不能被回收。</small>}
            <button className="primary-button" disabled={!selected || !reasonCode || reason.trim().length < 2 || Boolean(busy)} onClick={() => void createRequest()}>{busy === "create" ? <LoaderCircle className="spin" /> : <Gavel />}提交双人复核</button>
          </article>}

          <article className="panel security-queue-panel">
            <div className="panel-header"><div><span className="heading-icon"><Gavel /></span><span><h2>角色变更队列</h2><p>敏感原因正文不在列表接口返回</p></span></div></div>
            <div className="review-filters"><label>状态<select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}><option value="ALL">全部</option><option value="PENDING">待复核</option><option value="APPLIED">已生效</option><option value="REJECTED">已拒绝</option><option value="EXPIRED">已过期</option></select></label></div>
            {loading && <div className="empty compact-empty"><LoaderCircle className="spin" /><p>正在读取安全队列…</p></div>}
            {!loading && requests.length === 0 && <div className="empty compact-empty"><ShieldCheck /><p>当前筛选下没有角色变更申请。</p></div>}
            <div className="security-request-list">{requests.map((item) => (
              <article key={item.id}>
                <div className="security-request-head"><span><b>{actionNames[item.action]} · {roleNames[item.role]}</b><small>{item.targetAccount.maskedEmail} · {shortId(item.targetAccount.id)}</small></span><i className={`review-status ${item.status.toLowerCase()}`}>{statusNames[item.status]}</i></div>
                <div className="security-request-meta"><span><small>发起人</small><b>{item.requester.maskedEmail}</b></span><span><small>原因代码</small><code>{item.reasonCode}</code></span><span><small>申请时间</small><b>{formatTime(item.requestedAt)}</b></span><span><small>失效时间</small><b>{formatTime(item.expiresAt)}</b></span></div>
                {item.decider && <small className="security-decider">复核人：{item.decider.maskedEmail} · {formatTime(item.decidedAt)}</small>}
                {item.status === "PENDING" && canManage && item.canCurrentActorDecide && <div className="security-decision-box">
                  <label>拒绝原因代码<input value={rejectReasonCode} maxLength={64} onChange={(event) => setRejectReasonCode(event.target.value.toUpperCase().replace(/[^A-Z0-9_:-]/gu, ""))} /></label>
                  <label>拒绝说明<textarea value={rejectReason} maxLength={1000} onChange={(event) => setRejectReason(event.target.value)} placeholder="仅拒绝时必填；密文保存" /></label>
                  <div><button className="primary-button" disabled={Boolean(busy)} onClick={() => void decide(item, "APPROVE")}>{busy === `APPROVE:${item.id}` ? <LoaderCircle className="spin" /> : <Check />}同意并原子生效</button><button className="secondary-button danger-button" disabled={Boolean(busy) || rejectReason.trim().length < 2 || !rejectReasonCode} onClick={() => void decide(item, "REJECT")}>{busy === `REJECT:${item.id}` ? <LoaderCircle className="spin" /> : <X />}拒绝</button></div>
                </div>}
                {item.status === "PENDING" && !item.canCurrentActorDecide && <small className="decision-hint"><CircleAlert />你是该申请的发起人或目标账号，不能参与复核。</small>}
              </article>
            ))}</div>
          </article>
        </section>
        {canManage && <section className="security-grid">
          <article className="panel security-create-panel">
            <div className="panel-header"><div><span className="heading-icon"><ShieldOff /></span><span><h2>发起身份强制撤销</h2><p>这里只创建待复核申请；另一名安全管理员同意后才原子生效。</p></span></div></div>
            <div className="security-form-grid"><label>主体类型<select value={revocationSubjectType} onChange={(event) => setRevocationSubjectType(event.target.value as typeof revocationSubjectType)}><option value="PERSONAL">个人身份</option><option value="ORGANIZATION">企业身份</option></select></label><label>撤销类型<select value={revocationType} onChange={(event) => setRevocationType(event.target.value as typeof revocationType)}><option value="RISK_CONTROL">风险控制</option><option value="LEGAL_OR_REGULATORY">司法或监管</option><option value="ORGANIZATION_DISSOLVED">企业注销</option></select></label><label className="wide">主体 UUID<input value={revocationSubjectId} onChange={(event) => setRevocationSubjectId(event.target.value)} placeholder="个人身份 ID 或企业 ID" /></label><label>原因代码<input value={reasonCode} onChange={(event) => setReasonCode(event.target.value.toUpperCase().replace(/[^A-Z0-9_:-]/gu, ""))} /></label><label className="wide">加密说明<textarea value={reason} onChange={(event) => setReason(event.target.value)} /></label></div>
            <button className="primary-button" disabled={Boolean(busy) || !revocationSubjectId.trim() || !reasonCode || reason.trim().length < 2} onClick={() => void createRevocation()}>{busy === "create-revocation" ? <LoaderCircle className="spin" /> : <ShieldOff />}提交双人复核</button>
          </article>
          <article className="panel security-queue-panel">
            <div className="panel-header"><div><span className="heading-icon"><ShieldOff /></span><span><h2>身份撤销队列</h2><p>历史存证和链上事实不会删除</p></span></div><button className="secondary-button" onClick={() => void loadRevocations()}><RefreshCw />刷新</button></div>
            {revocations.length === 0 ? <div className="empty compact-empty"><ShieldCheck /><p>当前没有身份撤销申请。</p></div> : <div className="security-request-list">{revocations.map((item) => <article key={item.id}><div className="security-request-head"><span><b>{item.subjectType === "PERSONAL" ? "个人身份" : "企业身份"} · {item.revocationType}</b><small>{shortId(item.personalIdentityId ?? item.organizationId ?? item.id)} · {formatTime(item.requestedAt)}</small></span><i className={`review-status ${item.status.toLowerCase()}`}>{item.status}</i></div><div className="security-request-meta"><span><small>原因代码</small><code>{item.reasonCode}</code></span><span><small>申请人</small><b>{shortId(item.requestedByAccountId)}</b></span><span><small>冷静期截止</small><b>{formatTime(item.coolingUntil)}</b></span></div>{item.canCurrentActorDecide && <div className="security-decision-box"><div><button className="primary-button" disabled={Boolean(busy)} onClick={() => void decideRevocation(item, "APPROVE")}><Check />同意并原子撤销</button><button className="secondary-button danger-button" disabled={Boolean(busy)} onClick={() => void decideRevocation(item, "REJECT")}><X />拒绝</button></div></div>}</article>)}</div>}
          </article>
        </section>}
        <section className="security-grid">
          <article className="panel security-queue-panel">
            <div className="panel-header"><div><span className="heading-icon"><ShieldCheck /></span><span><h2>高风险账号恢复</h2><p>身份复核、两名安全管理员、真实通知和 24 小时冷静期缺一不可</p></span></div><button className="secondary-button" onClick={() => void loadD09D10()}><RefreshCw />刷新</button></div>
            {mfaResets.length === 0 ? <div className="empty compact-empty"><ShieldCheck /><p>当前没有 MFA 管理员重置申请。</p></div> : <div className="security-request-list">{mfaResets.map((item) => <article key={item.id}><div className="security-request-head"><span><b>MFA 重置 · {shortId(item.targetAccountId)}</b><small>申请 {formatTime(item.requestedAt)} · 版本 {item.version}</small></span><i className={`review-status ${item.status.toLowerCase()}`}>{item.status}</i></div><div className="security-request-meta"><span><small>身份复核员</small><b>{item.identityReviewedByAccountId ? shortId(item.identityReviewedByAccountId) : "待复核"}</b></span><span><small>安全发起人</small><b>{item.initiatedBySecurityAdminId ? shortId(item.initiatedBySecurityAdminId) : "待发起"}</b></span><span><small>通知验收</small><b>{item.notificationsVerifiedAt ? formatTime(item.notificationsVerifiedAt) : "未通过"}</b></span><span><small>冷静期截止</small><b>{formatTime(item.coolingUntil)}</b></span></div>{item.canCurrentActorInitiate && <button className="primary-button" disabled={Boolean(busy)} onClick={() => void advanceMfaReset(item, "INITIATE")}><Gavel />提交第二人安全复核</button>}{item.canCurrentActorDecide && <div className="security-decision-box"><div><button className="primary-button" disabled={Boolean(busy)} onClick={() => void advanceMfaReset(item, "APPROVE")}><Check />批准并验证通知门禁</button><button className="secondary-button danger-button" disabled={Boolean(busy)} onClick={() => void advanceMfaReset(item, "REJECT")}><X />拒绝</button></div></div>}</article>)}</div>}
          </article>

          <article className="panel security-create-panel">
            <div className="panel-header"><div><span className="heading-icon"><Gavel /></span><span><h2>生产集成 POC 门禁</h2><p>配置指纹、证据摘要、双人批准和运行适配器四层同时成立</p></span></div><span className={`status ${gateReadiness?.ready ? "status-received" : "status-warning"}`}>{gateReadiness?.ready ? "READY" : "BLOCKED"}</span></div>
            <div className="security-request-list">{gateReadiness?.components.map((item) => <article key={item.component}><div className="security-request-head"><span><b>{item.component}</b><small>{item.provider}</small></span><i className={`review-status ${item.reason === "READY" ? "passed" : "pending"}`}>{item.reason}</i></div><div className="security-request-meta"><span><small>非本地配置</small><b>{item.configured ? "是" : "否"}</b></span><span><small>POC 双人批准</small><b>{item.gatePassed ? "是" : "否"}</b></span><span><small>运行适配器</small><b>{item.runtimeConnected ? "已接通" : "未接通"}</b></span></div></article>)}</div>
            {canManage && <div className="security-form-grid"><label>组件<select value={gateComponent} onChange={(event) => setGateComponent(event.target.value as GateComponent)}><option value="OBJECT_STORAGE">对象存储</option><option value="MALWARE_SCANNER">恶意文件扫描</option><option value="KMS_HSM">KMS / HSM</option><option value="MFA_NOTIFICATION">MFA 恢复通知</option><option value="CONSORTIUM_CHAIN">联盟链 POC</option></select></label><label>环境<select value={gateEnvironment} onChange={(event) => setGateEnvironment(event.target.value as typeof gateEnvironment)}><option value="STAGING">预生产</option><option value="PRODUCTION">生产</option></select></label><label className="wide">POC 证据 SHA-256<input className="mono-input" maxLength={64} value={gateEvidenceDigest} onChange={(event) => setGateEvidenceDigest(event.target.value.toLowerCase().replace(/[^0-9a-f]/gu, ""))} /></label><label className="wide">证据引用<input value={gateEvidenceRef} maxLength={512} onChange={(event) => setGateEvidenceRef(event.target.value)} placeholder="受控文档编号、对象键或验收记录地址" /></label><button className="primary-button" disabled={Boolean(busy) || !/^[0-9a-f]{64}$/u.test(gateEvidenceDigest) || gateEvidenceRef.trim().length < 3} onClick={() => void createProductionGate()}><Gavel />提交第二人批准</button></div>}
            <div className="security-request-list">{productionGates.map((item) => <article key={item.id}><div className="security-request-head"><span><b>{item.component} · {item.provider}</b><small>{item.evidenceRef} · {formatTime(item.requestedAt)}</small></span><i className={`review-status ${item.status.toLowerCase()}`}>{item.status}</i></div><div className="security-request-meta"><span><small>配置匹配</small><b>{item.matchesCurrentConfiguration ? "是" : "否"}</b></span><span><small>证据摘要</small><code>{shortId(item.evidenceDigest)}</code></span></div>{item.canCurrentActorDecide && <div className="security-decision-box"><div><button className="primary-button" disabled={Boolean(busy)} onClick={() => void decideProductionGate(item, "PASS")}><Check />确认 POC 证据</button><button className="secondary-button danger-button" disabled={Boolean(busy)} onClick={() => void decideProductionGate(item, "REJECT")}><X />拒绝</button></div></div>}</article>)}</div>
          </article>
        </section>
        <footer className="workspace-footer"><span>链脉信枢 · 身份运营与生产安全门禁 v0.7 D09＋D10</span><span>lianmai.net.cn · 京ICP备2026024994号-1</span></footer>
      </main>
    </div>
  );
}
